/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/* #Modify! : add custom js */
/* group class .product-form__quantity and  .product-form__buy-buttons into a div with class product_actions when loaded */
document.addEventListener('DOMContentLoaded', function() {
    // Handle main product forms
    const productForms = document.querySelectorAll('.product-form:not(quick-buy-drawer .product-form):not(quick-buy-popover .product-form)');

    productForms.forEach(function(form) {
        const quantity = form.querySelector('.product-form__quantity');
        const buyButtons = form.querySelector('.product-form__buy-buttons');

        if (quantity && buyButtons) {
            const wrapper = document.createElement('div');
            wrapper.classList.add('product_actions');

            // Insert wrapper before the first element
            quantity.parentNode.insertBefore(wrapper, quantity);

            // Move both elements into the wrapper
            wrapper.appendChild(quantity);
            wrapper.appendChild(buyButtons);
        }
    });

    /* Handle each quick-buy-drawer and quick-buy-popover separately */
    const quickBuyDrawers = document.querySelectorAll('quick-buy-drawer');
    quickBuyDrawers.forEach(function(drawer) {
        // Function to initialize selling plan widget
        const initializeSellingPlanWidget = (form) => {
            const quantity = form.querySelector('.product-form__quantity');
            const buyButtons = form.querySelector('.product-form__buy-buttons');

            if (quantity && buyButtons && !quantity.closest('.product_actions')) {
                const wrapper = document.createElement('div');
                wrapper.classList.add('product_actions');

                // Insert wrapper before the first element
                quantity.parentNode.insertBefore(wrapper, quantity);

                // Move both elements into the wrapper
                wrapper.appendChild(quantity);
                wrapper.appendChild(buyButtons);
            }
        };

        // Initial setup for existing forms
        const productForms = drawer.querySelectorAll('.product-form');
        productForms.forEach(initializeSellingPlanWidget);

        // Watch for changes in the drawer content
        const observer = new MutationObserver(function(mutations) {
            mutations.forEach(function(mutation) {
                if (mutation.addedNodes.length) {
                    // Check for new product forms
                    mutation.addedNodes.forEach(function(node) {
                        if (node.nodeType === 1) { // Element node
                            const forms = node.querySelectorAll ? node.querySelectorAll('.product-form') : [];
                            forms.forEach(initializeSellingPlanWidget);
                        }
                    });
                }
            });
        });

        // Start observing the drawer for changes
        observer.observe(drawer, {
            childList: true,
            subtree: true
        });

        // Listen for drawer open events
        drawer.addEventListener('open', function() {
            const forms = drawer.querySelectorAll('.product-form');
            forms.forEach(initializeSellingPlanWidget);
        });
    });

    /* Handle quick-buy-popover elements */
    const quickBuyPopovers = document.querySelectorAll('quick-buy-popover');
    quickBuyPopovers.forEach(function(popover) {
        // Function to initialize selling plan widget
        const initializeSellingPlanWidget = (form) => {
            const quantity = form.querySelector('.product-form__quantity');
            const buyButtons = form.querySelector('.product-form__buy-buttons');

            if (quantity && buyButtons && !quantity.closest('.product_actions')) {
                const wrapper = document.createElement('div');
                wrapper.classList.add('product_actions');

                // Insert wrapper before the first element
                quantity.parentNode.insertBefore(wrapper, quantity);

                // Move both elements into the wrapper
                wrapper.appendChild(quantity);
                wrapper.appendChild(buyButtons);
            }
        };

        // Initial setup for existing forms
        const productForms = popover.querySelectorAll('.product-form');
        productForms.forEach(initializeSellingPlanWidget);

        // Watch for changes in the popover content
        const observer = new MutationObserver(function(mutations) {
            mutations.forEach(function(mutation) {
                if (mutation.addedNodes.length) {
                    // Check for new product forms
                    mutation.addedNodes.forEach(function(node) {
                        if (node.nodeType === 1) { // Element node
                            const forms = node.querySelectorAll ? node.querySelectorAll('.product-form') : [];
                            forms.forEach(initializeSellingPlanWidget);
                        }
                    });
                }
            });
        });

        // Start observing the popover for changes
        observer.observe(popover, {
            childList: true,
            subtree: true
        });

        // Listen for popover open events
        popover.addEventListener('open', function() {
            const forms = popover.querySelectorAll('.product-form');
            forms.forEach(initializeSellingPlanWidget);
        });
    });

});

// for each element clicked
document.addEventListener("click", function(event) {
    var element = event.target;
    var elementClass = element.className;
    if (elementClass === "block-swatch__item") {
        setTimeout( function () {
            window.location.reload();
        }, 100);
    }
});

  
document.addEventListener("change", function(event) {
    var element = event.target;
    var elementClass = element.className;
    if (elementClass === "product-form__select") {
        setTimeout( function () {
            window.location.reload();
        }, 100);
    }
    if (element.classList.contains("block-swatch__radio")) {
        setTimeout(() => {
            window.location.reload();
        }, 100);
    }
});

document.addEventListener('variant:changed', function(event) {
  const variant = event.detail.variant;
  if (variant && variant.id) {
    setTimeout(() => {
      window.location.reload();
    }, 200);
  }
});

// Fix Rebuy Smart Cart double add-to-cart issue
// Based on: https://status.rebuyengine.com/incidents/6k0ssf56rg7r
(function() {
  let isProcessing = false;
  let processingTimeout = null;

  // Intercept form submissions before Rebuy
  document.addEventListener('submit', function(event) {
    const form = event.target;

    // Only handle product forms
    if (!form.matches('[is="product-form"]') &&
        !form.matches('form[action*="/cart/add"]')) {
      return;
    }

    // Prevent duplicate submission
    if (isProcessing) {
      console.log('[Fix] Prevented duplicate cart submission');
      event.preventDefault();
      event.stopImmediatePropagation();
      return false;
    }

    // Set processing flag
    isProcessing = true;

    // Clear any existing timeout
    if (processingTimeout) {
      clearTimeout(processingTimeout);
    }

    // Reset flag after 2 seconds
    processingTimeout = setTimeout(() => {
      isProcessing = false;
      processingTimeout = null;
    }, 2000);

  }, true); // Use capture phase to run BEFORE Rebuy

  // Also listen for variant:added event (theme's custom event)
  let lastAddTimestamp = 0;
  document.addEventListener('variant:added', function(event) {
    const now = Date.now();

    // If less than 500ms since last add, it's likely a duplicate
    if (now - lastAddTimestamp < 500) {
      console.log('[Fix] Detected duplicate variant:added event, ignoring');
      event.stopImmediatePropagation();
      return false;
    }

    lastAddTimestamp = now;
  }, true);
})();